import React, { useCallback, useEffect, useState } from 'react';
import { useSessionStorage } from 'react-use';
import useAsyncEffect from 'use-async-effect';
import {
  Experience,
  ExperienceFilter,
  ExperienceSort,
  ExperienceStatus,
  ListOptions,
  sortByPublishDateDesc,
  useExperienceApi,
} from '@/webapi/use-experience-api';
import { usePagination } from '@/webapi/use-pagination';
import { LS_FILTER_KEY, LS_SEARCH_KEY } from '@/utils/consts';
import { CheckboxOption } from '@/components/checkbox-popover';
import {
  CustomMetric,
  useCustomGoalsApi,
} from '@/features/global-rules/custom-goals/api';
import { useFeatureBit } from '@/features/account-context';
import { PAGE_LAYOUT_ID } from '@/components/page-layout';
import { FeatureBit } from '@/webapi/use-auth-api';

export const MyExperiencesContext = React.createContext({} as MyExperiencesCtx);

const LS_SORT_KEY = `VSLY_DASHBOARD_SORT`;

export function newMyExperiencesContext(): MyExperiencesCtx {
  const pageSize = 12;
  const {
    listExperiences,
    archiveExperience,
    unarchiveExperience,
    unpublishExperience,
    publishExperience,
    deleteExperience,
    duplicateExperience,
    loading: apiLoading,
  } = useExperienceApi();
  const [customGoals, setCustomGoals] = useState<Array<CustomMetric>>([]);
  const { getCustomGoals } = useCustomGoalsApi();
  useAsyncEffect(async () => {
    const customGoals = await getCustomGoals();
    setCustomGoals(customGoals?.metrics || []);
  }, []);

  const [searchText, setSearchText] = useSessionStorage(LS_SEARCH_KEY, ``);
  const [sort, setSort] = useSessionStorage(LS_SORT_KEY, sortByPublishDateDesc);
  const [filter, setFilter] = useSessionStorage(
    LS_FILTER_KEY,
    ExperienceFilter.UNKNOWN_STATUS,
  );

  const { listLabels } = useExperienceApi();
  const [labelsState, setLabelsState] = useState<CheckboxOption[]>(undefined);
  const labelsGetter = useCallback(async () => {
    const labels = await listLabels();
    if (!labels) {
      return [];
    }
    return labels?.map((label) => ({ caption: label, selected: false }));
  }, []);

  const {
    total,
    current,
    maxPages,
    loading,
    setPage,
    setArgs,
    page,
    setCurrent,
    setTotal,
  } = usePagination(listExperiences, pageSize, {
    search: searchText,
    filter,
    sort,
  } as ListOptions);

  useEffect(() => {
    const labels =
      labelsState?.filter((l) => l.selected)?.map((l) => l.caption) ||
      undefined;
    setArgs({ search: searchText, filter, sort, labels } as ListOptions);
  }, [searchText, filter, sort, labelsState]);

  const appendRow = (experience: Experience) => {
    let list = current?.experiences;
    if (list && list.length > 0) {
      list = [experience, ...list];
    } else {
      list = [experience];
    }
    setCurrent({
      experiences: list,
      total: list.length,
      running: current?.running || 0,
      draft: current?.draft || 0,
      archived: current?.archived || 0,
      abTests: current?.abTests || 0,
      unfilteredTotal: current?.unfilteredTotal || 0,
      paused: current?.paused || 0,
    });
    setTotal(list.length);
  };

  const updateRow = (experience: Experience) => {
    if (current?.experiences && current.experiences.length > 0) {
      current.experiences = current.experiences.map((item) => {
        if (item.id === experience.id) {
          return experience;
        }
        return item;
      });
      setCurrent({
        running: current?.running || 0,
        draft: current?.draft || 0,
        archived: current?.archived || 0,
        paused: current?.paused || 0,
        total: current.experiences.length,
        experiences: current.experiences,
        abTests: current?.abTests || 0,
        unfilteredTotal: current?.unfilteredTotal || 0,
      });
    }
  };

  const removeRow = (expId: string) => {
    if (current?.experiences && current.experiences.length > 0) {
      const next = current.experiences.filter((item) => item.id !== expId);
      const removed = current.experiences.find((item) => item.id === expId);
      const draft = ExperienceStatus.DRAFT;
      const archived = ExperienceStatus.ARCHIVED;
      const draftOffset = removed.status === draft ? -1 : 0;
      const archivedOffset = removed.status === archived ? -1 : 0;
      setCurrent({
        running: current?.running || 0,
        paused: current?.paused || 0,
        draft: (current?.draft || 0) + draftOffset,
        archived: (current?.archived || 0) + archivedOffset,
        total: total - 1,
        unfilteredTotal: (current?.unfilteredTotal || 0) - 1,
        experiences: next,
      });
      setTotal(total - 1);
    }
  };

  const archive = async (exp: Experience): Promise<void> => {
    await archiveExperience(exp.id);
    exp.status = ExperienceStatus.ARCHIVED;
    exp.endDate = new Date().toISOString();
    exp.archivedAt = exp.endDate;
    updateRow(exp);
  };

  const unarchive = async (exp: Experience): Promise<void> => {
    await unarchiveExperience(exp.id);
    exp.status = ExperienceStatus.PAUSED;
    exp.endDate = new Date().toISOString();
    exp.archivedAt = undefined;
    updateRow(exp);
  };

  const unpublish = async (exp: Experience): Promise<void> => {
    await unpublishExperience(exp.id);
    exp.status = ExperienceStatus.PAUSED;
    exp.endDate = new Date().toISOString();
    updateRow(exp);
  };

  const publish = async (
    exp: Experience,
    resetStats?: boolean,
  ): Promise<void> => {
    await publishExperience(exp.id, resetStats);
    exp.status = ExperienceStatus.PUBLISHED;
    exp.publishedAt = new Date().toISOString();
    updateRow(exp);
  };

  const remove = async (exp: Experience): Promise<void> => {
    await deleteExperience(exp.id);
    removeRow(exp.id);
  };

  const duplicate = async (exp: Experience, name: string): Promise<void> => {
    const cloned = await duplicateExperience(exp, name);
    appendRow(cloned);
  };
  const isExpDetailsEnhancement = useFeatureBit(
    FeatureBit.IS_EXPERIENCE_DETAILS_ENHANCEMENT,
  );
  const isExpListImprovements = useFeatureBit(
    FeatureBit.EXPERIENCE_LIST_IMPROVEMENTS,
  );
  const isScrolled = useIsScrolled();

  return {
    isScrolled,
    updateRow,
    filter,
    sort,
    searchText,
    setFilter,
    setSort,
    setSearchText,
    setPage,
    total,
    running: current?.running || 0,
    draft: current?.draft || 0,
    archived: current?.archived || 0,
    paused: current?.paused || 0,
    abTests: current?.abTests || 0,
    oneHundredPercent: current?.oneHundredPercent || 0,
    unfilteredTotal: current?.unfilteredTotal || 0,
    page,
    maxPages,
    pageSize,
    loading: loading || apiLoading,
    experiencesList: current?.experiences,
    archive,
    unarchive,
    unpublish,
    publish,
    remove,
    duplicate,
    setLabelsState,
    labelsState,
    labelsGetter,
    customGoals,
    isExpDetailsEnhancement,
    isExpListImprovements,
  };
}

export interface MyExperiencesCtx {
  updateRow: (experience: Experience) => void;
  filter: ExperienceFilter;
  sort: ExperienceSort;
  searchText: string;
  setFilter: (filter: ExperienceFilter) => void;
  setSort: (sort: ExperienceSort) => void;
  setSearchText: (text: string) => void;
  setPage: (page: number) => void;
  total: number;
  running: number;
  archived: number;
  draft: number;
  paused: number;
  abTests: number;
  oneHundredPercent: number;
  unfilteredTotal: number;
  page: number;
  maxPages: number;
  pageSize: number;
  loading: boolean;
  experiencesList: Experience[];
  archive: (exp: Experience) => Promise<void>;
  unarchive: (exp: Experience) => Promise<void>;
  unpublish: (exp: Experience) => Promise<void>;
  publish: (exp: Experience, resetStats?: boolean) => Promise<void>;
  remove: (exp: Experience) => Promise<void>;
  duplicate: (exp: Experience, name: string) => Promise<void>;
  setLabelsState: React.Dispatch<React.SetStateAction<CheckboxOption[]>>;
  labelsState: CheckboxOption[];
  labelsGetter: () => Promise<CheckboxOption[]>;
  customGoals: CustomMetric[];
  isExpDetailsEnhancement: boolean;
  isExpListImprovements: boolean;
  isScrolled: boolean;
}

const useIsScrolled = () => {
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const el = document.getElementById(PAGE_LAYOUT_ID);
    if (el) {
      const onScroll = () => setScrollY(el.scrollTop);
      el.addEventListener(`scroll`, onScroll);
      return () => el.removeEventListener(`scroll`, onScroll);
    }
    return undefined;
  }, []);

  return scrollY > 50;
};
